/**
 * Where to send someone whose session ended, and where to send them back after
 * they sign in again. Kept pure so the Apollo error link, the router guard and
 * the login page all agree.
 */

export type LoginReason = 'expired' | 'signed_out';

export const LOGIN_REASON_MESSAGES: Record<LoginReason, string> = {
  expired: 'Your session has expired. Please sign in again to continue.',
  signed_out: 'You were signed out. Please sign in again.',
};

/** Signed-in areas. Everything else (login, public forms, payments, applicant status…) is public. */
const PROTECTED_PREFIXES = ['/admin', '/student', '/parent', '/switch-school', '/select-school'];

/** Pre-login guardian pages that live inside the /parent tree. */
const GUARDIAN_PUBLIC_PREFIXES = ['/parent/register', '/parent/accept-invitation'];

function startsWithSegment(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`);
}

export function isProtectedPath(path: string): boolean {
  if (GUARDIAN_PUBLIC_PREFIXES.some(prefix => startsWithSegment(path, prefix))) return false;
  return PROTECTED_PREFIXES.some(prefix => startsWithSegment(path, prefix));
}

/** The signed-in guardian portal (not its public register / invitation pages). */
function isGuardianPortalPath(path: string): boolean {
  return startsWithSegment(path, '/parent') && isProtectedPath(path);
}

/**
 * A same-origin, in-app path that is safe to navigate to after login, or null.
 * Rejects absolute URLs, protocol-relative ("//evil.com") and backslash tricks
 * so the redirect parameter can never become an open redirect.
 */
export function safeRedirect(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return null;
  if (/^\/[a-z][a-z0-9+.-]*:/i.test(value)) return null;
  if (startsWithSegment(value, '/login')) return null;
  return value;
}

export function loginLocation(reason?: LoginReason, redirect?: string | null): string {
  const params = new URLSearchParams();
  if (reason) params.set('reason', reason);
  const target = safeRedirect(redirect);
  if (target) params.set('redirect', target);
  const query = params.toString();
  return query ? `/login?${query}` : '/login';
}

export function loginReasonMessage(reason: unknown): string | null {
  return typeof reason === 'string' && reason in LOGIN_REASON_MESSAGES
    ? LOGIN_REASON_MESSAGES[reason as LoginReason]
    : null;
}

/**
 * The redirect to honour after login, only if it belongs to the signed-in
 * role's area. Staff and guardians are both `User` accounts, so a User may
 * return to /admin or to the guardian portal; the router's portal guard then
 * sends a target in a portal they lack to their own home.
 */
export function postLoginTarget(redirect: unknown, role: 'Student' | 'User'): string | null {
  const target = safeRedirect(redirect);
  if (!target) return null;
  if (role === 'Student') return startsWithSegment(target, '/student') ? target : null;
  return startsWithSegment(target, '/admin') || isGuardianPortalPath(target) ? target : null;
}

/**
 * `path` with a staff member's or guardian's post-login redirect carried
 * along. Multi-school accounts pass through /select-school and a full reload of /switch-school before
 * reaching the app, so each of those hops has to hand the redirect on.
 */
export function withStaffRedirect(path: string, redirect: unknown): string {
  const target = postLoginTarget(redirect, 'User');
  return target ? `${path}?${new URLSearchParams({ redirect: target })}` : path;
}
